import React, { useRef, useState } from 'react';
import {
  Container,
  Stack,
  Typography,
  Card,
  Box,
  Alert,
  CircularProgress,
  TextField,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  MenuItem,
  FormControlLabel,
  Checkbox,
} from '@mui/material';
import QrReader from 'react-qr-reader-es6';
import QRCode from 'react-qr-code';
import { toast } from 'react-toastify';
import api from '../../api';

const UPI_ID = 'upi://pay?pa=zaheenkasim@okicici&pn=zaheen%20kasim&aid=uGICAgIDA8puPYA';

const getDeviceLocation = () =>
  new Promise((resolve, reject) => {
    if (!navigator.geolocation) {
      reject(new Error('Geolocation is not supported on this device.'));
      return;
    }

    navigator.geolocation.getCurrentPosition(
      (position) => {
        resolve({
          latitude: position.coords.latitude,
          longitude: position.coords.longitude,
        });
      },
      (error) => {
        reject(new Error(error?.message || 'Unable to fetch device location.'));
      },
      { enableHighAccuracy: true, timeout: 10000, maximumAge: 0 }
    );
  });

export default function DeliveredScanner() {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isLoadingDetails, setIsLoadingDetails] = useState(false);
  const [manualConsignmentNo, setManualConsignmentNo] = useState('');
  const [lastScanned, setLastScanned] = useState('');
  const [showQr, setShowQr] = useState(false);
  const [packageData, setPackageData] = useState(null);
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [deliveryData, setDeliveryData] = useState({
    deliveryStatus: 'DELIVERED',
    remarks: '',
    receivedBy: '',
    receiverContactNo: '',
    moneyReceived: false,
    amountReceived: '',
    receivedType: 'upi',
  });
  const lastScanAtRef = useRef(0);

  const fetchPackageDetails = async (consignmentNo) => {
    if (!consignmentNo) return;
    try {
      setIsLoadingDetails(true);
      const response = await api.post('/api/v1/deliveries/package_detail', {
        data: { consignmentNo },
      });

      if (response?.data?.success && response?.data?.package) {
        const pkg = response.data.package;
        setPackageData(pkg);
        setLastScanned(consignmentNo);
        setDeliveryData((prev) => ({
          ...prev,
          deliveryStatus: 'DELIVERED',
          remarks: '',
          receiverContactNo: '',
          moneyReceived: Boolean(pkg?.toPay),
          amountReceived: pkg?.toPay ? String(pkg?.totalAmount || '') : '',
        }));
        setDetailsOpen(true);
      } else {
        toast.error(response?.data?.message || 'Package not found.');
      }
    } catch (error) {
      toast.error(error?.response?.data?.message || error?.message || 'Failed to fetch package details.');
    } finally {
      setIsLoadingDetails(false);
    }
  };

  const handleScan = async (data) => {
    if (!data || isSubmitting || isLoadingDetails || detailsOpen) return;

    const now = Date.now();
    if (now - lastScanAtRef.current < 200) return;

    const rawValue = typeof data === 'string' ? data : data?.text;
    const consignmentNo = String(rawValue || '').trim();
    if (!consignmentNo) return;

    lastScanAtRef.current = now;
    await fetchPackageDetails(consignmentNo);
  };

  const handleError = (err) => {
    console.error(err);
  };

  const onClickManualFetch = async () => {
    const consignmentNo = String(manualConsignmentNo || '').trim();
    if (!consignmentNo) {
      toast.error('Please enter consignment number.');
      return;
    }
    await fetchPackageDetails(consignmentNo);
    setManualConsignmentNo('');
  };

  const onChangeDeliveryData = (event) => {
    const { name, value, checked, type } = event.target;
    setDeliveryData((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
  };

  const onClickSubmitDelivered = async () => {
    if (!packageData?.scanCode) {
      toast.error('Scan code not found.');
      return;
    }
    const isDelivered = deliveryData.deliveryStatus === 'DELIVERED';
    if (isDelivered && !String(deliveryData.receivedBy || '').trim()) {
      toast.error('Received By is required for delivered status.');
      return;
    }

    const isToPay = Boolean(packageData?.toPay);
    if (isToPay) {
      if (!deliveryData.amountReceived || !deliveryData.receivedType) {
        toast.error('Please fill payment details.');
        return;
      }
    }
    if (deliveryData.deliveryStatus !== 'DELIVERED' && !String(deliveryData.remarks || '').trim()) {
      toast.error('Please enter remarks for selected delivery status.');
      return;
    }

    try {
      setIsSubmitting(true);
      const { latitude, longitude } = await getDeviceLocation();
      const payload = {
        data: {
          consignmentNo: packageData.scanCode,
          remarks: deliveryData.deliveryStatus === 'DELIVERED' ? '' : String(deliveryData.remarks || '').trim(),
          deliveryStatus: deliveryData.deliveryStatus,
          receivedBy: isDelivered ? String(deliveryData.receivedBy || '').trim() : '',
          receiverContactNo: deliveryData.receiverContactNo,
          latitude,
          longitude,
          moneyReceived: isToPay ? deliveryData.moneyReceived : false,
          amountReceived: isToPay ? Number(deliveryData.amountReceived || 0) : 0,
          receivedType: isToPay ? deliveryData.receivedType : null,
        },
      };

      const response = await api.post('/api/v1/deliveries/delivered', payload);
      if (response?.data?.success) {
        toast.success(response?.data?.message || 'Package marked as delivered.');
        setDetailsOpen(false);
        setShowQr(false);
        setPackageData(null);
      } else {
        toast.error(response?.data?.message || 'Failed to mark delivered.');
      }
    } catch (error) {
      toast.error(error?.response?.data?.message || error?.message || 'Failed to mark delivered.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const callReceiver = () => {
    const phone = packageData?.consigneeContactNo || '';
    if (!phone) {
      toast.error('Phone number not available.');
      return;
    }
    window.location.href = `tel:${phone}`;
  };

  return (
    <Container maxWidth="sm">
      <Stack direction="row" alignItems="center" justifyContent="space-between" mb={5}>
        <Typography variant="h4" gutterBottom>
          Delivered
        </Typography>
      </Stack>

      <Card sx={{ p: { xs: 2, sm: 3 } }}>
        <Stack spacing={2}>
          <Typography variant="h6">QR Code Scanner</Typography>
          <Typography variant="body2" color="text.secondary">
            Scan package to fetch details and mark it as delivered.
          </Typography>

          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
            <TextField
              label="Manual Consignment Number"
              value={manualConsignmentNo}
              onChange={(e) => setManualConsignmentNo(e.target.value)}
              fullWidth
            />
            <Button variant="contained" onClick={onClickManualFetch} disabled={isLoadingDetails || isSubmitting}>
              Fetch
            </Button>
          </Stack>

          <Box
            sx={{
              width: '100%',
              maxWidth: 420,
              mx: 'auto',
              borderRadius: 2,
              overflow: 'hidden',
              border: '1px solid',
              borderColor: 'divider',
              bgcolor: 'common.black',
            }}
          >
            <QrReader
              delay={250}
              onError={handleError}
              onScan={detailsOpen ? () => {} : handleScan}
              constraints={{ facingMode: { ideal: 'environment' } }}
              style={{ width: '100%' }}
            />
          </Box>

          {isLoadingDetails && (
            <Stack direction="row" spacing={1} alignItems="center">
              <CircularProgress size={18} />
              <Typography variant="body2" color="text.secondary">
                Fetching package details...
              </Typography>
            </Stack>
          )}

          {lastScanned && (
            <Alert severity="success">
              Last scanned consignment: <b>{lastScanned}</b>
            </Alert>
          )}
        </Stack>
      </Card>

      <Dialog open={detailsOpen} fullWidth maxWidth="md" onClose={() => setDetailsOpen(false)}>
        <DialogTitle>Delivery Details</DialogTitle>
        <DialogContent>
          {packageData && (
            <Stack spacing={2} sx={{ mt: 1 }}>
              <TextField label="Consignment No" value={packageData?.consignmentNo || ''} InputProps={{ readOnly: true }} fullWidth />
              <TextField label="Consignee Name" value={packageData?.consigneeName || ''} InputProps={{ readOnly: true }} fullWidth />
              <TextField label="Consignee Address" value={packageData?.consigneeAddress || ''} InputProps={{ readOnly: true }} fullWidth />
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
                <TextField label="Consignee Contact No" value={packageData?.consigneeContactNo || ''} InputProps={{ readOnly: true }} fullWidth />
                <Button variant="outlined" onClick={callReceiver}>
                  Call
                </Button>
              </Stack>
              <TextField label="Sender Name" value={packageData?.senderName || ''} InputProps={{ readOnly: true }} fullWidth />
              <TextField label="To Pay" value={packageData?.toPay ? 'Yes' : 'No'} InputProps={{ readOnly: true }} fullWidth />
              {packageData?.toPay && (
                <TextField label="Total Amount" value={packageData?.totalAmount || ''} InputProps={{ readOnly: true }} fullWidth />
              )}
              <TextField
                select
                label="Delivery Status"
                name="deliveryStatus"
                value={deliveryData.deliveryStatus}
                onChange={onChangeDeliveryData}
                fullWidth
              >
                <MenuItem value="DELIVERED">DELIVERED</MenuItem>
                <MenuItem value="DELIVERY_FAILED">DELIVERY_FAILED</MenuItem>
                <MenuItem value="RESCHEDULED">RESCHEDULED</MenuItem>
                <MenuItem value="RETURNED">RETURNED</MenuItem>
              </TextField>
              {deliveryData.deliveryStatus !== 'DELIVERED' && (
                <TextField
                  label="Remarks"
                  name="remarks"
                  value={deliveryData.remarks}
                  onChange={onChangeDeliveryData}
                  fullWidth
                />
              )}
              <TextField
                label="Received By"
                name="receivedBy"
                value={deliveryData.receivedBy}
                onChange={onChangeDeliveryData}
                fullWidth
              />
              <TextField
                label="Receiver Contact No"
                name="receiverContactNo"
                value={deliveryData.receiverContactNo}
                onChange={onChangeDeliveryData}
                fullWidth
              />

              {packageData?.toPay && (
                <>
                  <FormControlLabel
                    control={
                      <Checkbox
                        name="moneyReceived"
                        checked={Boolean(deliveryData.moneyReceived)}
                        onChange={onChangeDeliveryData}
                      />
                    }
                    label="Money Received"
                  />
                  <TextField
                    label="Amount Received"
                    name="amountReceived"
                    type="number"
                    value={deliveryData.amountReceived}
                    onChange={onChangeDeliveryData}
                    fullWidth
                  />
                  <TextField
                    select
                    label="Received Type"
                    name="receivedType"
                    value={deliveryData.receivedType}
                    onChange={onChangeDeliveryData}
                    fullWidth
                  >
                    <MenuItem value="upi">upi</MenuItem>
                    <MenuItem value="cash">cash</MenuItem>
                    <MenuItem value="in bank account">in bank account</MenuItem>
                  </TextField>
                  <Button variant="outlined" onClick={() => setShowQr((prev) => !prev)}>
                    {showQr ? 'Hide UPI QR' : 'Show UPI QR'}
                  </Button>
                  {showQr && (
                    <Stack spacing={1} alignItems="center">
                      <QRCode value={UPI_ID} size={180} />
                      <Typography variant="body2">{UPI_ID}</Typography>
                    </Stack>
                  )}
                </>
              )}
            </Stack>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDetailsOpen(false)} variant="outlined" disabled={isSubmitting}>
            Close
          </Button>
          <Button onClick={onClickSubmitDelivered} variant="contained" disabled={isSubmitting}>
            {isSubmitting ? 'Submitting...' : 'Submit'}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
}
